import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useSearchParams, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import 'flag-icons/css/flag-icons.min.css';

/** Rend tous les toasts Sonner copiables au clic. */
function CopyableToaster(props) {
  useEffect(() => {
    const onClickToast = (e) => {
      const toast = e.target.closest('[data-sonner-toast]');
      if (!toast) return;
      // Ne pas déclencher si c'est un bouton (close, action)
      if (e.target.closest('button')) return;
      const parts = [
        toast.querySelector('[data-title]')?.textContent,
        toast.querySelector('[data-description]')?.textContent,
      ].filter(Boolean).join('\n').trim();
      const text = parts || toast.innerText?.trim();
      if (!text) return;

      // Tentative clipboard API (HTTPS requis)
      const doCopy = () => {
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(text).then(() => {
            toast.style.outline = '2px solid #38bdf8';
            toast.setAttribute('title', 'Copié !');
            setTimeout(() => {
              toast.style.outline = '';
              toast.removeAttribute('title');
            }, 800);
          }).catch(() => fallbackCopy());
        } else {
          fallbackCopy();
        }
      };

      // Fallback : sélection + execCommand
      const fallbackCopy = () => {
        try {
          const sel = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(toast);
          sel.removeAllRanges();
          sel.addRange(range);
          document.execCommand('copy');
          sel.removeAllRanges();
          toast.style.outline = '2px solid #38bdf8';
          setTimeout(() => { toast.style.outline = ''; }, 800);
        } catch (_) {}
      };

      doCopy();
    };
    document.addEventListener('click', onClickToast);
    return () => document.removeEventListener('click', onClickToast);
  }, []);
  return <Toaster {...props} />;
}
import './index.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocieteProvider } from './context/SocieteContext';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import Materiels from './pages/Materiels';
import MarquesModeles from './pages/MarquesModeles';
import ArchitectureApp from './pages/settings/ArchitectureApp';
import AdminLogs from './pages/settings/AdminLogs';
import CronJobs from './pages/settings/CronJobs';
import Announcements from './pages/settings/Announcements';
import Planning from './pages/Planning';
import Annuaire from './pages/Annuaire';
import Atelier from './pages/Atelier';
import SocietesAgences from './pages/SocietesAgences';
import ParametresGeneraux from './pages/settings/ParametresGeneraux';
import JoursFeries from './pages/settings/JoursFeries';
import Numerotation from './pages/settings/Numerotation';
import ConditionsPaiement from './pages/settings/ConditionsPaiement';
import TypesDocuments from './pages/settings/TypesDocuments';
import TypesFichiers from './pages/settings/TypesFichiers';
import ModeleEmails from './pages/settings/ModeleEmails';
import Integrations from './pages/settings/Integrations';
import ApiKeys from './pages/settings/ApiKeys';
import Maintenance from './pages/Maintenance';
import PlansEntretien from './pages/PlansEntretien';
import Articles from './pages/Articles';
import Familles from './pages/Familles';
import UnitesContenants from './pages/UnitesContenants';
import CaracTechniques from './pages/CaracTechniques';
import Layout from './components/Layout';
import PdfGenerators from './pages/settings/PdfGenerators';
import SettingsLayout from './pages/settings/SettingsLayout';
import Organisation from './pages/settings/Organisation';
import Utilisateurs from './pages/settings/Utilisateurs';
import Comptabilite from './pages/settings/Comptabilite';import Financements from './pages/Financements';
import RapportsMateriels from './pages/RapportsMateriels';
import RapportsRelances   from './pages/RapportsRelances';
import PotentielLocatif from './pages/PotentielLocatif';
import BalancesAgees from './pages/BalancesAgees';
import PalmaresClients from './pages/PalmaresClients';
import ActivatePage from './pages/ActivatePage';
import ControlesPeriodiques from './pages/ControlesPeriodiques';
import Comptages from './pages/Comptages';
import Stocks from './pages/Stocks';
import StocksValorisation from './pages/StocksValorisation';
import PrevisionsTresorerie from './pages/PrevisionsTresorerie';
import MouvementsZone from './pages/MouvementsZone';
import MouvementsStock from './pages/MouvementsStock';
import Inventaires from './pages/Inventaires';
import Transferts from './pages/Transferts';
import Achats from './pages/Achats';
import BonsReception from './pages/BonsReception';
import Acomptes from './pages/Acomptes';
import Locations from './pages/Locations';
import LocationsV2 from './pages/LocationsV2';
import Livraisons from './pages/Livraisons';
import GammesLocation from './pages/GammesLocation';
import CriteresControle from './pages/CriteresControle';
import FacturationTabs from './pages/FacturationTabs';
import TerrainBonsLivraison from './pages/terrain/TerrainBonsLivraison';
import TerrainCommandesVente from './pages/terrain/TerrainCommandesVente';
import Reassort from './pages/Reassort';
import DossiersRevient from './pages/DossiersRevient';
import TerrainHome from './pages/terrain/TerrainHome';
import { TerrainComptagesList, TerrainComptageDetail } from './pages/terrain/TerrainComptages';
import TerrainSignatures from './pages/terrain/TerrainSignatures';
import TerrainAtelier from './pages/terrain/TerrainAtelier';
import LocationSignaturePage from './pages/LocationSignaturePage';
import LocationV2SignaturePage from './pages/LocationV2SignaturePage';
import VenteSignaturePage from './pages/VenteSignaturePage';
import CommandeSignaturePage from './pages/CommandeSignaturePage';
import BonsLivraisonVentes from './pages/BonsLivraisonVentes';
import RetoursVentes from './pages/RetoursVentes';
import FacturesAchat from './pages/FacturesAchat';
import FacturesDistribution from './pages/FacturesDistribution';
import FacturesReparation from './pages/FacturesReparation';
import FacturesClient from './pages/FacturesClient';
import ReglementsClient from './pages/ReglementsClient';
import AtelierDemarrageSignaturePage from './pages/AtelierDemarrageSignaturePage';
import AtelierSignaturePage from './pages/AtelierSignaturePage';
import AtelierFicheInterventionPage from './pages/AtelierFicheInterventionPage';
import Employes from './pages/Employes';
import FonctionsInternes from './pages/FonctionsInternes';
import TableauDeCharge from './pages/TableauDeCharge';
import TempsNonFacturables from './pages/TempsNonFacturables';
import TransfertComptable from './pages/TransfertComptable';
import EmailingLayout from './pages/EmailingLayout';
import EmailCampagnes from './pages/EmailCampagnes';
import EmailAudiences from './pages/EmailAudiences';
import EmailLogs from './pages/EmailLogs';
import EmailModeles from './pages/EmailModeles';
import Ventes from './pages/Ventes';
import VerifyEmailPage from './pages/VerifyEmailPage';
import { googleSession } from './api';
import { UpdateBanner } from './components/UpdateBanner';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Terrain Route — accès dédié tablette, vérifie les droits terrain
function TerrainRoute() {
  const { user, loading, menuRights } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9FAFB' }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const isAdmin = user.role === 'admin_org' || user.role === 'super_admin';
  const hasAnyTerrainRight = isAdmin || (
    Array.isArray(menuRights) && menuRights.some(r => r.startsWith('terrain_'))
  );
  if (!hasAnyTerrainRight) return <Navigate to="/dashboard" replace />;

  return <Outlet />;
}

// Protection par droit pour les sous-routes terrain
function TerrainRequireRight({ right }) {
  const { user, menuRights } = useAuth();
  const isAdmin = user?.role === 'admin_org' || user?.role === 'super_admin';
  if (isAdmin) return <Outlet />;
  if (menuRights === null) return null; // chargement en cours
  if (!Array.isArray(menuRights) || !menuRights.includes(right)) {
    return <Navigate to="/terrain" replace />;
  }
  return <Outlet />;
}

// Admin Route — réservé aux admin_org et super_admin, redirige sinon
function AdminRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin_org' && user.role !== 'super_admin') {
    return <Navigate to="/settings/organisation" replace />;
  }
  return <Outlet />;
}

// Google OAuth callback handler
function GoogleCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      googleSession(sessionId)
        .then(res => {
          localStorage.setItem('token', res.data.token);
          checkAuth().then(() => navigate('/dashboard', { replace: true }));
        })
        .catch(() => navigate('/login', { replace: true }));
    } else {
      navigate('/login', { replace: true });
    }
  }, [searchParams, navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
      <div className="text-center">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4" style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        <p style={{ color: 'var(--text-muted)' }}>Connexion en cours...</p>
      </div>
    </div>
  );
}

// Placeholder pages
function PlaceholderPage({ title }) {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--primary-50)' }}>
        <span className="text-2xl">🚧</span>
      </div>
      <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--primary-solid)',  }}>
        {title}
      </h1>
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Cette fonctionnalité sera disponible prochainement</p>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SocieteProvider>
          <CopyableToaster position="top-right" richColors toastOptions={{ dismissible: false, style: { userSelect: 'text', webkitUserSelect: 'text', cursor: 'text' } }} />
        <Routes>
          {/* Auth routes */}
          <Route path="/login" element={<AuthPage />} />
          <Route path="/register" element={<AuthPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/activate" element={<ActivatePage />} />
          <Route path="/auth/callback" element={<GoogleCallback />} />
          {/* Route publique — signature web (sans auth) */}
          <Route path="/signer/:token" element={<LocationSignaturePage />} />
          <Route path="/signer-v2/:token" element={<LocationV2SignaturePage />} />
          <Route path="/ventes/signer/:token" element={<VenteSignaturePage />} />
          <Route path="/signer-commande/:token" element={<CommandeSignaturePage />} />
          <Route path="/atelier/signer/:token" element={<AtelierSignaturePage />} />
          <Route path="/atelier/fiche/:token"  element={<AtelierFicheInterventionPage />} />
          <Route path="/atelier/demarrer/:token" element={<AtelierDemarrageSignaturePage />} />
          
          {/* Protected routes */}
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            
            {/* Location */}
            <Route path="location/planning" element={<Planning />} />
            <Route path="contrats" element={<Locations />} />
            <Route path="location/livraisons" element={<Livraisons />} />
            <Route path="location/gammes" element={<GammesLocation />} />
            <Route path="location/controles" element={<CriteresControle />} />
            <Route path="location/facturation" element={<FacturationTabs />} />
            <Route path="location-v2/contrats" element={<LocationsV2 />} />
            
            {/* Atelier */}
            <Route path="atelier" element={<Atelier />} />
            <Route path="atelier/maintenance" element={<Maintenance />} />
            <Route path="atelier/plans-entretien" element={<Maintenance />} />
            
            
            {/* Distribution */}
            <Route path="ventes" element={<Ventes />} />
            <Route path="bons-livraison-ventes" element={<BonsLivraisonVentes />} />
            <Route path="retours-ventes" element={<RetoursVentes />} />
            <Route path="factures-achat" element={<FacturesAchat />} />
            <Route path="factures-distribution" element={<FacturesDistribution />} />
            <Route path="atelier/factures" element={<FacturesReparation />} />
            <Route path="commandes" element={<PlaceholderPage title="Commandes" />} />
            <Route path="facturation" element={<Navigate to="/facturation/factures" replace />} />
            <Route path="facturation/factures" element={<FacturesClient />} />
            <Route path="facturation/reglements" element={<ReglementsClient />} />
            
            {/* Matériels */}
            <Route path="materiels" element={<Materiels />} />
            <Route path="marques-modeles" element={<MarquesModeles />} />
            <Route path="materiels/controles-periodiques" element={<ControlesPeriodiques />} />
            <Route path="materiels/comptages" element={<Comptages />} />
            
            {/* Catalogue */}
            <Route path="articles" element={<Articles />} />
            <Route path="familles" element={<Familles />} />
            <Route path="unites-contenants" element={<UnitesContenants />} />
            <Route path="carac-techniques" element={<CaracTechniques />} />
            
            {/* Financement */}
            <Route path="financements" element={<Financements />} />

            {/* Stocks */}
            <Route path="stocks" element={<Stocks />} />
            <Route path="stocks/valorisation" element={<StocksValorisation />} />
            <Route path="rapports/previsions" element={<PrevisionsTresorerie />} />
            <Route path="mouvements-zone" element={<MouvementsZone />} />
            <Route path="mouvements-stock" element={<MouvementsStock />} />
            <Route path="inventaires" element={<Inventaires />} />
            <Route path="transferts" element={<Transferts />} />

            {/* Achats */}
            <Route path="achats" element={<Achats />} />
            <Route path="achats/reassort" element={<Reassort />} />
            <Route path="achats/acomptes" element={<Acomptes />} />
            <Route path="achats/dossiers-revient" element={<DossiersRevient />} />
            <Route path="bons-reception" element={<BonsReception />} />

            {/* Other */}
            <Route path="annuaire" element={<Annuaire />} />
            <Route path="rapports">
              <Route index element={<Navigate to="/rapports/materiels" replace />} />
              <Route path="materiels" element={<RapportsMateriels />} />
              <Route path="potentiel-locatif" element={<PotentielLocatif />} />
              <Route path="balances-agees" element={<BalancesAgees />} />
              <Route path="palmares-clients" element={<PalmaresClients />} />
              <Route path="relances"         element={<RapportsRelances />} />
            </Route>

            {/* Ressources Humaines */}
            <Route path="rh">
              <Route index element={<Navigate to="/rh/employes" replace />} />
              <Route path="employes" element={<Employes />} />
              <Route path="fonctions-internes" element={<FonctionsInternes />} />
              <Route path="tableau-de-charge" element={<TableauDeCharge />} />
              <Route path="tnf" element={<TempsNonFacturables />} />
            </Route>

            {/* Modèles d'emails — accessible à tous les utilisateurs authentifiés */}
            <Route path="mes-modeles-emails" element={<ModeleEmails />} />

            {/* Transfert Comptable — admin uniquement, page dédiée */}
            <Route element={<AdminRoute />}>
              <Route path="transfert-comptable" element={<TransfertComptable />} />
            </Route>

            {/* Emailing */}
            <Route path="emailing" element={<EmailingLayout />}>
              <Route index element={<Navigate to="/emailing/campagnes" replace />} />
              <Route path="campagnes" element={<EmailCampagnes />} />
              <Route path="audiences" element={<EmailAudiences />} />
              <Route path="modeles"   element={<EmailModeles />} />
              <Route path="logs"      element={<EmailLogs />} />
            </Route>

            {/* Paramètres */}
            <Route path="settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="/settings/organisation" replace />} />
              <Route path="organisation" element={<Organisation />} />
              <Route path="utilisateurs" element={<Utilisateurs />} />
              <Route path="societes" element={<SocietesAgences />} />
              <Route path="parametres-generaux" element={<ParametresGeneraux />} />
              <Route path="jours-feries" element={<JoursFeries />} />
              <Route path="numerotation" element={<Numerotation />} />
              <Route path="conditions-paiement" element={<ConditionsPaiement />} />
              <Route path="types-documents" element={<TypesDocuments />} />
              <Route path="types-fichiers"        element={<TypesFichiers />} />
              <Route path="modeles-emails"        element={<Navigate to="/emailing/modeles" replace />} />
              <Route path="integrations"          element={<Integrations />} />
              <Route path="api-keys"              element={<ApiKeys />} />
              <Route path="comptabilite"          element={<Comptabilite />} />
              <Route path="exercices-comptables"  element={<Comptabilite />} />
              {/* Routes Administration — admin_org et super_admin uniquement */}
              <Route element={<AdminRoute />}>
                <Route path="announcements"         element={<Announcements />} />
                <Route path="emails-systeme"        element={<ModeleEmails mode="systeme" />} />
                <Route path="pdf-generators"        element={<PdfGenerators />} />
                <Route path="cron-jobs"             element={<CronJobs />} />
                <Route path="architecture"          element={<ArchitectureApp />} />
                <Route path="logs"                  element={<AdminLogs />} />
              </Route>
            </Route>
            {/* Redirect legacy depuis l'ancien chemin settings */}
            <Route path="settings/transfert-comptable" element={<Navigate to="/transfert-comptable" replace />} />
            <Route path="parametres/societes" element={<Navigate to="/settings/societes" replace />} />
          </Route>
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />

          {/* Routes Terrain — interface tablette, sans sidebar */}
          <Route path="terrain" element={<TerrainRoute />}>
            <Route index element={<TerrainHome />} />
            <Route element={<TerrainRequireRight right="terrain_comptages" />}>
              <Route path="comptages" element={<TerrainComptagesList />} />
              <Route path="comptages/:id" element={<TerrainComptageDetail />} />
            </Route>
            <Route element={<TerrainRequireRight right="terrain_contrats_location" />}>
              <Route path="signatures" element={<TerrainSignatures />} />
            </Route>
            <Route element={<TerrainRequireRight right="terrain_bons_livraison" />}>
              <Route path="bons-livraison" element={<TerrainBonsLivraison />} />
            </Route>
            <Route element={<TerrainRequireRight right="terrain_commandes_vente" />}>
              <Route path="commandes-vente" element={<TerrainCommandesVente />} />
            </Route>
            <Route element={<TerrainRequireRight right="terrain_atelier" />}>
              <Route path="atelier" element={<TerrainAtelier />} />
            </Route>
          </Route>
        </Routes>
        </SocieteProvider>
      </AuthProvider>
      <UpdateBanner />
    </BrowserRouter>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);

// Enregistrer le Service Worker pour les notifications push (PWA) uniquement
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(`${process.env.PUBLIC_URL}/sw.js`, { scope: '/' })
      .then(() => console.log('[SW] Enregistré'))
      .catch((e) => console.warn('[SW] Erreur:', e));
  });
}
